import { EditorFile } from "@/apps/forge/EditorFile";
import { EventListenerModel } from "@/apps/forge/EventListenerModel";
import { TabStoreState } from "@/apps/forge/interfaces/TabStoreState";
import { 
  TabBIKPlayerState, TabGFFEditorState, TabHexEditorState, TabImageViewerState, TabModelViewerState, 
  TabModuleEditorState, TabQuickStartState, TabSSFEditorState, TabTLKEditorState, TabTwoDAEditorState, 
  TabDLGEditorState, TabUTCEditorState, TabUTDEditorState, TabUTPEditorState, TabSaveGameEditorState, TabState
} from "@/apps/forge/states/tabs";

export type TabManagerEventListenerTypes =
  'onTabAdded'|'onTabRemoved'|'onTabShow'|'onTabHide'|'onTabsReordered';

export interface TabManagerEventListeners {
  onTabAdded: Function[],
  onTabRemoved: Function[],
  onTabShow: Function[],
  onTabHide: Function[],
  onTabsReordered: Function[],
}

export class EditorTabManager extends EventListenerModel {
  currentTab?: TabState;
  tabs: TabState[] = [];

  static __tabId: number = 0;

  static GetNewTabID(): number {
    return EditorTabManager.__tabId++;
  }

  constructor(){
    super();
    this.currentTab = undefined;
    this.tabs = [];
  }

  addTab(tab: TabState){
    //Check to see if the tab has the singleInstance flag set to TRUE
    if(tab.singleInstance){
      if(this.tabTypeExists(tab)){
        this.getTabByType(tab.constructor.name)?.show();
        return; //Return because the TabManager can only have one of these
      }
    }

    let alreadyAdded = this.tabs.find( (_tab: TabState) => _tab.id == tab.id) ? true : false;
    if(alreadyAdded){
      console.warn('Tab already added to the TabManager', tab);
      return;
    }

    //Check to see if a tab is already editing this resource
    let alreadyOpen = this.isResourceIdOpenInTab(tab.getResourceID());
    if(alreadyOpen != null){
      //Show the tab that is already open
      alreadyOpen.show();
      //return so that the rest of the function is not called
      return;
    }

    this.currentTab = tab;
    tab.attach(this);
    tab.show();
    this.tabs.push(tab);

    this.processEventListener('onTabAdded');

    return tab;
  }

  removeTab(tab: TabState){
    const length = this.tabs.length;
    const tabIndex = this.tabs.indexOf(tab);

    for(let i = 0; i < length; i++){
      if(tab == this.tabs[i]){
        console.log('removeTab', 'Tab found. Deleting');
        tab.destroy();
        this.tabs.splice(i, 1);
        break;
      }
    }
    try{
      if(this.currentTab == tab){
        let tabIndexToSelect = tabIndex-1;
        if(tabIndexToSelect < 0) tabIndexToSelect = 0;
        if(this.tabs.length){
          console.log('removeTab', 'Current tab removed. Trying to show sibling child');
          const t = this.tabs[tabIndexToSelect];
          if(t){
            console.log(t);
            t.show();
          }
        }
      }
    }catch(e){ console.log(e); }

    this.processEventListener('onTabRemoved');

  }

  moveTab(fromIndex: number, toIndex: number){
    if(!Number.isInteger(fromIndex) || !Number.isInteger(toIndex)){
      return false;
    }
    const maxIndex = this.tabs.length - 1;
    if(fromIndex < 0 || toIndex < 0 || fromIndex > maxIndex || toIndex > maxIndex){
      return false;
    }
    if(fromIndex === toIndex){
      return false;
    }

    const [tab] = this.tabs.splice(fromIndex, 1);
    if(!tab){
      return false;
    }
    this.tabs.splice(toIndex, 0, tab);
    this.processEventListener('onTabsReordered', [fromIndex, toIndex, tab]);
    return true;
  }

  //Checks the supplied resource ID against all open tabs and returns tab if it is found
  isResourceIdOpenInTab(resID: number){

    if(resID){
      for(let i = 0; i < this.tabs.length; i++){
        if(this.tabs[i].getResourceID() == resID){
          return this.tabs[i];
        }
      }
    }

    return null;

  }

  getTabByType(tabClass: any){
    for(let i = 0; i < this.tabs.length; i++){
      if(this.tabs[i].constructor.name === tabClass)
        return this.tabs[i];
    }
    return;
  }

  tabTypeExists(tab: TabState){
    let tabClass = tab.constructor.name;
    for(let i = 0; i < this.tabs.length; i++){
      if(this.tabs[i].constructor.name === tabClass)
        return true;
    }
    return false;
  }

  hideAll(){
    for(let i = 0; i < this.tabs.length; i++){
      this.tabs[i].hide();
    }
  }

  /** Remove every tab without firing tab lifecycle events (used during app re-init / HMR). */
  clearAllTabs(){
    for(let i = this.tabs.length - 1; i >= 0; i--){
      this.tabs[i].destroy();
    }
    this.tabs.length = 0;
    this.currentTab = undefined;
  }

  restoreTabState(tabState: TabStoreState) {
    if(tabState.file){
      tabState.file = EditorFile.revive(tabState.file as Partial<EditorFile>);
      console.log('file', tabState.file);
    }
    switch(tabState.type){
      case 'TabQuickStartState':
        this.addTab(
          new TabQuickStartState({editorFile: tabState.file})
        );
      break;
      case 'TabImageViewerState':
        this.addTab(
          new TabImageViewerState({editorFile: tabState.file})
        );
      break;
      case 'TabModelViewerState':
        this.addTab(
          new TabModelViewerState({editorFile: tabState.file})
        );
      break;
      case 'TabGFFEditorState':
        this.addTab(
          new TabGFFEditorState({editorFile: tabState.file})
        );
      break;
      case 'TabModuleEditorState':
        // Restored via project settings.module_editor.open when a project is opened.
        // Never restore from global open_tabs.
      break;
      case 'TabTwoDAEditorState':
        this.addTab(
          new TabTwoDAEditorState({editorFile: tabState.file})
        );
      break;
      case 'TabSSFEditorState':
        this.addTab(
          new TabSSFEditorState({editorFile: tabState.file})
        );
      break;
      case 'TabTLKEditorState':
        this.addTab(
          new TabTLKEditorState({editorFile: tabState.file})
        );
      break;
      case 'TabHexEditorState':
        this.addTab(
          new TabHexEditorState({editorFile: tabState.file})
        );
      break;
      case 'TabUTCEditorState':
        this.addTab(
          new TabUTCEditorState({editorFile: tabState.file})
        );
      break;
      case 'TabDLGEditorState':
        this.addTab(
          new TabDLGEditorState({editorFile: tabState.file})
        );
      break;
      case 'TabUTDEditorState':
        this.addTab(
          new TabUTDEditorState({editorFile: tabState.file})
        );
      break;
      case 'TabUTPEditorState':
        this.addTab(
          new TabUTPEditorState({editorFile: tabState.file})
        );
      break;
      case 'TabSaveGameEditorState':
        this.addTab(
          new TabSaveGameEditorState({editorFile: tabState.file})
        );
      break;
      case 'TabBIKPlayerState':
        this.addTab(
          new TabBIKPlayerState({editorFile: tabState.file})
        );
      break;
    }
  }

}
